import TitleBar from '../../../components/TitleBar';
import React, { useEffect, useState } from 'react';
import { Footer } from '../../../components/common/Footer';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CREATE_LEVEL_DEPENDENCY_MATRIX,
  FETCH_LEVEL_LOCATIONS,
  UNCHECK_LOCATION,
  VALIDATE_UNCHECK_LOCATION,
} from '../queries';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  BorderedSection,
  Button,
  FilterHeader,
  Loading,
  SaveInstructions,
} from '../../../components';
import { LocationsSelection } from './DependencyMatrix';
import { message, notification } from 'antd';
import type { NotificationPlacement } from 'antd/es/notification/interface';
import { LOCATION_TYPES } from '../../../constants';
import ListManager from '../../../components/common/ListManager';
import { useUserContext } from '../../../context/PermissionsContext';
import { getLevelLocationData, updatePreviousLocations } from '../../../utils';
import TitleUpdater from '../../../components/common/TitleUpdater';

interface ValidationResponse {
  validateUnCheckedLocation: boolean;
}

type Location = {
  uuid: string;
  locationName: string;
  locationType: string;
};

function LevelLocations() {
  const [api, contextHolder] = notification.useNotification();

  const params = useParams();
  const divisionId = params.divisionId;
  const parentLocation = params.parentLocation;
  let [searchParams] = useSearchParams();
  const locationId = searchParams.get('locationId') || '';
  const level = parseInt(searchParams.get('level')!);
  const divisionName = searchParams.get('divisionName') || '';
  const locationName = searchParams.get('locationName') || '';
  const prevLocationId = searchParams.get('prevLocationId') || '';
  const prevLocationName = searchParams.get('prevLocationName') || '';

  const {
    prevLocationId: updatedPrevLocationId,
    prevLocationName: updatedPrevLocationName,
  } = updatePreviousLocations(
    locationId,
    locationName,
    prevLocationId,
    prevLocationName,
  );

  const { locationId: levelLocationId, locationName: levelLocationName } =
    getLevelLocationData(updatedPrevLocationId, updatedPrevLocationName, level);

  const [addedLocations, setAddedLocations] = useState<Location[]>([]);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);

  const { canEdit } = useUserContext();

  const orderedAvailableLocations = [...filteredLocations].sort((a, b) => {
    return a.locationName.localeCompare(b.locationName);
  });

  const orderedAddedLocations = [...addedLocations].sort((a, b) => {
    return a.locationName.localeCompare(b.locationName);
  });

  const [search, setSearch] = useState('');

  const { data, loading, error } = useQuery<LocationsSelection>(
    FETCH_LEVEL_LOCATIONS,
    {
      variables: {
        division: { uuid: divisionId },
        level: level,
        location: { uuid: levelLocationId },
        parentLocation: { uuid: parentLocation },
      },
      skip: !divisionId || !levelLocationId || !parentLocation,
    },
  );
  const [
    validateUncheck,
    { error: uncheckedError, loading: uncheckedLoading },
  ] = useLazyQuery<ValidationResponse>(VALIDATE_UNCHECK_LOCATION);

  const [CreateDependencyMatrix, { loading: createDependencyLoading }] =
    useMutation(CREATE_LEVEL_DEPENDENCY_MATRIX);

  const [uncheckLocation, { loading: uncheckDependencyLoading }] = useMutation(
    UNCHECK_LOCATION,
    {
      refetchQueries: ['fetchLevelLocations', 'FetchLevelLocationsV2'],
    },
  );

  const checkedLocations = data?.findLocationsDependencyLevel.locationAux || [];
  const uncheckedLocations =
    data?.findLocationsDependencyLevel.unselectedLocations.filter(
      (loc) => loc.locationType !== LOCATION_TYPES.DISTRIBUTION_CENTER,
    ) || [];

  const openNotification = (placement: NotificationPlacement) => {
    api.info({
      message: 'This location cannot be unselected',
      description:
        'In order to remove this location from the dependency chain, please make the dependency 0% on the next page and return to this screen to uncheck it.',
      placement,
      duration: 10,
      type: 'error',
    });
  };

  useEffect(() => {
    if (data) {
      setAddedLocations(checkedLocations);
      setAvailableLocations(uncheckedLocations);
      setFilteredLocations(uncheckedLocations);
    }
  }, [data]);

  const onAddItem = (item: Location) => {
    setAddedLocations((prev) => [...prev, item]);
    setAvailableLocations((prev) =>
      prev.filter((loc) => loc.uuid !== item.uuid),
    );
    setFilteredLocations((prev) =>
      prev.filter((loc) => loc.uuid !== item.uuid),
    );
  };

  const onRemoveItem = async (item: Location) => {
    try {
      const response = await validateUncheck({
        variables: {
          input: {
            locations: [{ uuid: item.uuid }],
            division: { uuid: divisionId },
            level: level,
            location: { uuid: locationId },
            parentLocation: { uuid: parentLocation },
          },
        },
      });

      if (response?.data?.validateUnCheckedLocation) {
        setAvailableLocations((prev) => [...prev, item]);
        setFilteredLocations((prev) => [...prev, item]);
        setAddedLocations((prev) =>
          prev.filter((loc) => loc.uuid !== item.uuid),
        );
        return;
      }

      openNotification('top');
    } catch (e) {
      message.error('Failed to uncheck location');
    }
  };

  const onSaveChanges = async () => {
    if (!canEdit) {
      message.info('You do not have permission to edit this data');
      return;
    }

    const currentAddedLocations = [...addedLocations];
    const currentAvailableLocations = [...availableLocations];

    try {
      const locationsToAdd = currentAddedLocations.map((loc) => ({
        uuid: loc.uuid,
      }));

      await CreateDependencyMatrix({
        variables: {
          input: {
            division: {
              uuid: divisionId,
            },
            level: level,
            location: {
              uuid: locationId,
            },
            supportLocations: locationsToAdd,
            parentLocation: { uuid: parentLocation },
          },
        },
      });
    } catch (error) {
      message.error('Failed to add locations');
    }

    if (currentAvailableLocations.length === uncheckedLocations.length) return;

    const locationsToUncheckDifference = currentAvailableLocations
      .filter(
        (loc) => !uncheckedLocations.some((uloc) => uloc.uuid === loc.uuid),
      )
      .map((loc) => ({
        uuid: loc.uuid,
      }));

    try {
      for (const loc of locationsToUncheckDifference) {
        await uncheckLocation({
          variables: {
            input: {
              locations: [loc],
              division: { uuid: divisionId },
              level: level,
              location: { uuid: locationId },
              parentLocation: { uuid: parentLocation },
            },
          },
        });
      }
    } catch (error) {
      message.error('Failed to remove locations');
    }

    message.success('Locations saved successfully');
  };

  const filterAvailableLocations = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    const filtered = availableLocations.filter((location) =>
      location.locationName.toLowerCase().includes(value),
    );
    setFilteredLocations(filtered);
  };

  let content;

  if (loading) {
    content = <Loading />;
  } else {
    content = (
      <ListManager<Location>
        addedItems={orderedAddedLocations}
        availableItems={orderedAvailableLocations}
        addedTitle="LOCATIONS ADDED"
        availableTitle="LOCATIONS AVAILABLE"
        onAddItem={onAddItem}
        onRemoveItem={onRemoveItem}
        getItemLabel={(item: Location) => item.locationName}
        infoContent={<SaveInstructions isLocation={!!locationName} />}
      />
    );
  }

  if (uncheckedError) return <>Error! {uncheckedError.message}</>;

  if (error) return <>Error! {error.message}</>;

  return (
    <>
      {contextHolder}
      <TitleUpdater title={`Level ${level} Location Selection`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`Level ${level} Locations // ${divisionName} // ${levelLocationName}`}
        brief="Select which of your locations provide materials or intermediates that are consumed by
                      Location. For example, if final assembly occurs at the receiving location, all locations that send
                       components should be checked."
      />
      <BorderedSection
        title=""
        noPadding
        headingClassName="h-20"
        contentClassName="py-4"
        extendOptions={
          <FilterHeader
            searchTerm={search}
            onSearch={filterAvailableLocations}
          />
        }
      >
        {content}
      </BorderedSection>
      <div className="flex items-center justify-center mt-4">
        <Button
          onClick={onSaveChanges}
          type="primary"
          className="text-sm mt-4 min-w-[180px]"
          disabled={
            uncheckedLoading ||
            createDependencyLoading ||
            uncheckDependencyLoading ||
            !canEdit
          }
        >
          Save
        </Button>
      </div>
      <Footer
        backPath={
          level === 1
            ? `/dependency-mapping/finished-goods-summary/${divisionId}?level=0&divisionName=${divisionName}`
            : `/dependency-mapping/${parentLocation}/level-summary/${divisionId}?level=${
                level - 1
              }&locationId=${prevLocationId}&divisionName=${divisionName}&locationName=${prevLocationName}&prevLocationId=${updatedPrevLocationId}&prevLocationName=${updatedPrevLocationName}`
        }
        nextPath={
          `/dependency-mapping/${divisionId}/${parentLocation}/level-mapping?level=${level}` +
          `&locationId=${locationId}` +
          `&locationName=${locationName}` +
          `&divisionName=${divisionName}` +
          `&prevLocationId=${prevLocationId || locationId}` +
          `&prevLocationName=${prevLocationName || locationName}`
        }
      />
    </>
  );
}

export default LevelLocations;
